import { FocusEvent, KeyboardEvent, useState } from 'react';
import {
  hasModifier,
  nextRadioIndex,
  radioIndexOf,
  radiosOf,
  resolveTabStopIndex,
  type RadioItem,
} from '../IdentityTypeRow/radioGroupNavigation';

type RovingRadioGroupInput = {
  /** The radios of the group in the order they appear on the page. */
  items: readonly RadioItem[];
  selectedId: string | null;
};

/**
 * Keyboard behaviour for a group of radios, the same as one IdentityTypeRow has: one tab stop (the
 * focused radio, else the chosen one, else the first available one), and the arrow keys move
 * focus along the group, wrapping at the ends. The arrow keys never choose. Spread
 * `groupHandlers` on the element with `role="radiogroup"` and give each radio `tabIndexOf(index)`.
 */
export function useRovingRadioGroup({ items, selectedId }: Readonly<RovingRadioGroupInput>) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const tabStopIndex = resolveTabStopIndex({ items, focusedIndex, selectedId });

  const groupHandlers = {
    onFocus: (event: FocusEvent<HTMLElement>) => {
      setFocusedIndex(radioIndexOf(radiosOf(event.currentTarget), event.target));
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setFocusedIndex(null);
      }
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (hasModifier(event)) {
        return;
      }
      const radios = radiosOf(event.currentTarget);
      const next = nextRadioIndex(event.key, radioIndexOf(radios, event.target), radios.length);
      if (next === null) {
        return;
      }
      event.preventDefault();
      radios[next].focus();
    },
  };

  const tabIndexOf = (index: number): 0 | -1 => (index === tabStopIndex ? 0 : -1);

  return { groupHandlers, tabIndexOf };
}
